import './PreviewPage.css';
import { useEffect, useState } from 'react';
import { useLocation, useHistory } from "react-router-dom";
import { useIonAlert, useIonRouter } from '@ionic/react';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { Typography } from '@mui/material'; 
// Functions
import { dataURLtoFile } from '../../exportFunctions/standardExportFunctions';
// Components
import IonicPageWrapper from '../../components/IonicPageWrapper/IonicPageWrapper';
import StandardPreviewCard from '../../components/StandardPreviewCard/StandardPreviewCard';
// ICONs & SVGs
import StandardButton from '../../components/StandardButton/StandardButton';
import { Preferences } from '@capacitor/preferences';

function PreviewPage() {
    const ionRouter = useIonRouter();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar()
    const { state } = useLocation();
    // console.log(state);
    const [presentAlert] = useIonAlert();

    const [btnLoading, setBtnLoading] = useState(false)
    const [saveLoading, setSaveLoading] = useState(false);

    const [userData, setUserData] = useState(null);
    const [role, setRole] = useState(null);
    const [previewCardData, setPreviewCardData] = useState(null);

    const [isDownloaded, setIsDownloaded] = useState(0);

    useEffect(() => {
        if (!state.cData) return ionRouter.goBack();
        console.log(state);
        onVerifyUserRole();
        mergeImageData();
    }, [state])

    const mergeImageData = () => {
        if (!state?.cData) {
            setPreviewCardData(null);
            return;
        }

        const cardDataCopy = JSON.parse(JSON.stringify(state.cData));
        const imgData = { ...state.img_data?.updateDataImage, ...state.img_data?.updateDataImageBack };
        const userData = state?.user_data || {};

        // Helper function to check if image is signature
        const isSignature = (img) => {
            return img?.signature || 
                   img?.tag?.toLocaleLowerCase() === "sign" || 
                   img?.tag?.toLocaleLowerCase() === "signature";
        };

        // Merge front images
        if (Object.keys(imgData).length > 0 && cardDataCopy?.view_data?.id_front?.image_objects) {
            cardDataCopy.view_data.id_front.image_objects = cardDataCopy.view_data.id_front.image_objects.map(img => {
                const imgKey = img.tag;
                if (imgData[imgKey]) {
                    // For signatures, ensure they fill the entire box
                    if (isSignature(img)) {
                        return { ...img, src: imgData[imgKey], objectFit: 'fill' };
                    }
                    return { ...img, src: imgData[imgKey] };
                }
                return img;
            });
        }

        // Merge back images
        if (Object.keys(imgData).length > 0 && cardDataCopy?.view_data?.id_back?.image_objects) {
            cardDataCopy.view_data.id_back.image_objects = cardDataCopy.view_data.id_back.image_objects.map(img => {
                const imgKey = img.tag;
                if (imgData[imgKey]) {
                    // For signatures, ensure they fill the entire box
                    if (isSignature(img)) {
                        return { ...img, src: imgData[imgKey], objectFit: 'fill' };
                    }
                    return { ...img, src: imgData[imgKey] };
                }
                return img;
            });
        }

        // Merge front text data
        if (Object.keys(userData).length > 0 && cardDataCopy?.view_data?.id_front?.text_objects) {
            cardDataCopy.view_data.id_front.text_objects = cardDataCopy.view_data.id_front.text_objects.map(text => {
                const textKey = text.tag;
                // If the key exists in userData, use it (even if it's empty string)
                if (textKey in userData && userData[textKey] !== undefined && userData[textKey] !== null) {
                    return { ...text, text: String(userData[textKey]) };
                }
                return text;
            });
        }

        // Merge back text data
        if (Object.keys(userData).length > 0 && cardDataCopy?.view_data?.id_back?.text_objects) {
            cardDataCopy.view_data.id_back.text_objects = cardDataCopy.view_data.id_back.text_objects.map(text => {
                const textKey = text.tag;
                // If the key exists in userData, use it (even if it's empty string)
                if (textKey in userData && userData[textKey] !== undefined && userData[textKey] !== null) {
                    return { ...text, text: String(userData[textKey]) };
                }
                return text;
            });
        }

        setPreviewCardData(cardDataCopy);
    }

    const filterImg = async (type) => {
        if (type == 'save') {
            setSaveLoading(true);
        } else {
            setBtnLoading(true);
        }

        await onUpdateUsername(); //username update

        let imgObj = { ...state.img_data?.updateDataImage, ...state.img_data?.updateDataImageBack }
        const imageObjectsCombinedArr = [...state?.cData?.view_data?.id_front?.image_objects, ...state?.cData?.view_data?.id_back?.image_objects]

        // Only upload images that are still data URLs (not already uploaded links)
        for (let i = 0; i < Object.keys(imgObj).length; i++) {
            const imgKey = Object.keys(imgObj)[i];
            const imgValue = imgObj[imgKey];

            // Check if image is a data URL (needs uploading) or already a link
            if (imgValue?.split(':')[0] == 'data') {
                const imageObj = imageObjectsCombinedArr?.find(img => img?.tag == imgKey);
                if (!imageObj) {
                    if (type == 'save') {
                        setSaveLoading(false);
                    } else {
                        setBtnLoading(false);
                    }
                    return enqueueSnackbar('Index Error Uploading Image !!!', { variant: 'error' });
                }

                // Get removeBG value from image object (1 if true, 0 if false)
                const removeBGValue = imageObj?.removeBG === true ? 1 : 0;

                let imgLink = await uploadImgToBucket(
                    dataURLtoFile(imgValue, state?.teacher_idCreate_userID || state.cData?.id),
                    removeBGValue,
                    type
                );

                if (!imgLink) {
                    if (type == 'save') {
                        setSaveLoading(false);
                    } else {
                        setBtnLoading(false);
                    }
                    return enqueueSnackbar('Error Uploading Image !!!', { variant: 'error' });
                }

                imgObj = { ...imgObj, [imgKey]: imgLink };
            }
            // If image is already a link (not a data URL), use it directly
        }

        if (type == 'save') {
            onSaveCard(imgObj)
        } else {
            onSendForVerification(imgObj);
        }
    }

    const uploadImgToBucket = async (img, removeBG, type) => {
        let link = ''
        console.log(img, removeBG, type);
        const fData = new FormData();
        fData.append('image', img);
        // Always append removeBG based on the passed value (1 or 0)
        fData.append('removeBG', removeBG);
        await axios.post(`${process.env.REACT_APP_BASE_URL}/user/createlink`, fData)
            .then(res => {
                if (res.data.success) {
                    link = res.data.image_link;
                } else {
                    enqueueSnackbar(res.data.message, { variant: 'error' });
                }
            })
            .catch(err => {
                console.log(err);
                enqueueSnackbar('Error, Please Try Again !!!', { variant: 'error' });
            })

        return link;
    }

    const removeUserPreference = async () => {
        await Preferences.remove({ key: 'gjaii_IDCARD_user' });
        await Preferences.remove({ key: 'userMobile' });
    };
    const onSendForVerification = async (img_data) => {
        let body = {};
        let url = '';
        const { value1 } = await Preferences.get({
            key: "gjaai_num10digit",
        });

        if (state?.cardUpdate) { // Update ID
            if (!state.mapped_id) return enqueueSnackbar('Something Went Wrong !!!', { variant: 'error' });

            url = `${process.env.REACT_APP_BASE_URL}/user/updatecardmapping`;
            body = {
                // is_verified: state?.teacherVerify_edit ? 1 : 0,
                is_verified: role === "parent" ? 0 : 1,
                id: state.mapped_id,
                user_data: {
                    ...state.user_data,
                    ...img_data,
                },
                viaForm: 1
            }
        } else { // Sending Verification Or Already Verified If Teacher is Creating
            const { value } = await Preferences.get({ key: 'gjaii_IDCARD_user' });

            if (!state.cData?.id) return enqueueSnackbar('Something Went Wrong !!!', { variant: 'error' });

            url = `${process.env.REACT_APP_BASE_URL}/user/createcardmap`;
            if (role === "parent") {
                body = {
                    icardid: state.cData?.id,
                    userid: state?.teacher_idCreate_userID || JSON.parse(value)?.id,
                    user_data: {
                        ...state.user_data,
                        ...img_data
                    },
                    viaForm: 1,
                    is_verified: 0
                }}else{
                    body = {
                    icardid: state.cData?.id,
                    userid: state?.teacher_idCreate_userID || JSON.parse(value)?.id,
                    user_data: {
                        ...state.user_data,
                        ...img_data
                    },
                    viaForm: 1,
                    is_verified: 1}
                }
        }

        await axios.post(url, body)
            .then(async res => {
                // console.log(res.data);
                if (res.data.success) {
                    if (state?.cardUpdate) {
                        if (state?.teacherVerify_edit) {
                            enqueueSnackbar('ID Card Approved Successfully !!!', { variant: 'success' });
                            ionRouter.push(state?.after_update_push || '/teacher/verify-id');
                        } else {
                            enqueueSnackbar('ID Card Updated Successfully !!!', { variant: 'success' });
                            // Remove user preferences from local storage
                            await removeUserPreference();
                            // Navigate based on role
                            if (role === "parent") {
                                ionRouter.push('/signup');
                            } else {
                                ionRouter.push('/login');
                            }
                            window.location.reload();
                        }
                    } else {
                        if (state?.teacher_idCreate) {
                            enqueueSnackbar(`ID Card Created Successfully !!!`, { variant: 'success' });
                            ionRouter.push("/teacher/create-id");
                        } else {
                            enqueueSnackbar('ID Card Ready For Print !!!', { variant: 'success' });
                            // Remove user preferences from local storage
                            await removeUserPreference();
                            // Navigate based on role
                            if (role === "parent") {
                                ionRouter.push('/signup');
                            } else {
                                ionRouter.push('/login');
                            }
                            window.location.reload();
                        }
                    }
                } else {
                    enqueueSnackbar(res.data.message, { variant: 'error' });
                }
                setBtnLoading(false);
            })
            .catch(err => {
                console.log(err);
                setBtnLoading(false);
                enqueueSnackbar('Error, Please Try Again !!!', { variant: 'error' });
            })
    }

    const onSaveCard = async (img_data) => {
        let body = {};
        let url = '';

        if (state?.cardUpdate) { // Update ID
            if (!state.mapped_id) return enqueueSnackbar('Something Went Wrong !!!', { variant: 'error' });

            url = `${process.env.REACT_APP_BASE_URL}/user/updatecardmapping`;
            body = {
                is_verified: state?.teacherVerify_edit ? 0 : 2,
                id: state.mapped_id,
                user_data: {
                    ...state.user_data,
                    ...img_data,
                },
                viaForm: 1
            }
        } else { // Sending Verification Or Already Verified If Teacher is Creating
            const { value } = await Preferences.get({ key: 'gjaii_IDCARD_user' });

            if (!state.cData?.id) return enqueueSnackbar('Something Went Wrong !!!', { variant: 'error' });

            url = `${process.env.REACT_APP_BASE_URL}/user/createcardmap`;
            body = {
                icardid: state.cData?.id,
                userid: state?.teacher_idCreate_userID || JSON.parse(value)?.id,
                user_data: {
                    ...state.user_data,
                    ...img_data,
                },
                viaForm: 1,
                is_verified: 2
            }
        }

        await axios.post(url, body)
            .then(async res => {
                if (res.data.success) {
                    enqueueSnackbar('ID Card Saved !!!', { variant: 'success' });
                    // if (state?.cardUpdate) {
                    //     ionRouter.push(state?.after_update_push);
                    // } else {
                    //     if (state?.teacher_idCreate) {
                    //         ionRouter.push("/teacher/create-id");
                    //     } else {
                    //         ionRouter.push("/idcard-verification-status");
                    //     }
                    // }
                    // ionRouter.goBack();
                    // ionRouter.push("/update", "back", "pop", { ...state, cardUpdate: true, mapped_id: res.data?.mapped_id });
                    console.log(state?.cardUpdate);
                    if (state?.cardUpdate) {
                        ionRouter.push("/login", "back", "pop");
                    } else {
                        ionRouter.push("/signup", "back", "pop");
                    }

                } else {
                    enqueueSnackbar(res.data.message, { variant: 'error' });
                }
                setSaveLoading(false);
            })
            .catch(err => {
                console.log(err);
                setSaveLoading(false);
                enqueueSnackbar('Error, Please Try Again !!!', { variant: 'error' });
            })
    }

    const onVerifyUserRole = async () => {
        const { value } = await Preferences.get({ key: 'gjaii_IDCARD_user' });
        setUserData(JSON.parse(value));

        // Check if role is "parent" and set removeBG to 0
        const { value: roleValue } = await Preferences.get({ key: 'gjaai_role' });
        setRole(roleValue);
        const { value: isDownloadedValue } = await Preferences.get({ key: 'is_downloaded' });
        setIsDownloaded(isDownloadedValue);
    }

    const onUpdateUsername = async () => {
        if (!state?.cData?.view_data?.id_front?.text_objects?.[0]?.text) return;
        const { value } = await Preferences.get({ key: "gjaii_IDCARD_user" });
        // alert(state?.teacher_idCreate_userID || JSON.parse(value)?.id);
        const body = {
            user_id: state?.teacher_idCreate_userID || JSON.parse(value)?.id,
            first_name:
                state?.cData?.view_data?.id_front?.text_objects?.[0]?.text,
        };

        await axios.post(`${process.env.REACT_APP_BASE_URL}/user/updateusername`, body)
            .then(async res => {
                if (res.data.success) {
                    console.log('username upadated');
                    // Update local Preferences after successful username update
                    const updatedUser = { ...JSON.parse(value), first_name: body.first_name };
                    await Preferences.set({ key: "gjaii_IDCARD_user", value: JSON.stringify(updatedUser) });
                } else {
                    enqueueSnackbar(res.data.message, { variant: 'error' });
                    setSaveLoading(false);
                    setBtnLoading(false);
                    throw new Error('Update User Name Error !!!');
                }
            })
            .catch(err => {
                console.log(err);
                enqueueSnackbar('Error, Please Try Again !!!', { variant: 'error' });
                setSaveLoading(false);
                setBtnLoading(false);
            })
    }

    const onFinalForPrint = () => {
        // Check if role is student and validate all images are uploaded
        if (role === "student") {
            const imgObj = { ...state.img_data?.updateDataImage, ...state.img_data?.updateDataImageBack };
            const imageObjectsCombinedArr = [...state?.cData?.view_data?.id_front?.image_objects, ...state?.cData?.view_data?.id_back?.image_objects];

            // Check if any image is still a data URL (not uploaded) or missing
            const missingImages = [];
            imageObjectsCombinedArr.forEach(imageObj => {
                // Skip admin images
                if (imageObj?.adminImage) return;

                const imgKey = imageObj.tag;
                const imgValue = imgObj[imgKey] || imageObj?.src;

                // Check if image is missing, empty, or still a data URL (needs uploading)
                if (!imgValue || imgValue.trim() === '' || imgValue?.split(':')[0] === 'data') {
                    missingImages.push(imgKey);
                }
            });

            if (missingImages.length > 0) {
                return enqueueSnackbar(`Please upload all images before submitting. Missing: ${missingImages.join(', ')}`, { variant: 'error' });
            }
        }

        presentAlert({
            mode: 'ios',
            header: 'Are you sure ?',
            subHeader: role === "student" ? 'You want to save this and Final For Print' : 'You want to submit this and Save',
            buttons: [
                {
                    text: 'Cancel',
                    handler: () => console.log('reject'),
                },
                {
                    text: 'OK',
                    handler: () => filterImg("submit"),
                    cssClass: 'preview_ID_ionBtn_ok',
                },
            ],
        })
    }

    return (
        <IonicPageWrapper headerTitle="Preview IDCard" onBackClick={state?.teacherVerify_edit ? null : () => ionRouter.push("/update", "back", "pop")}>
            <div id="preview">
                <section className="preview_cont">
                    <div className="previewCard_cont">
                        <StandardPreviewCard cardData={previewCardData || state?.cData} />
                    </div>

                    <div
                        className="preview_btnCont"
                        style={{ display: state?.teacherVerify_edit ? "block" : "flex" }}
                    >
                        {/* {((!(state?.verified == 0) && (userData?.role == 'student')) || (userData?.role == 'teacher')) && (
                            <>
                                <StandardButton
                                    label={"Save and Back"}
                                    variant="outlined"
                                    bg="transparent"
                                    disabled={btnLoading || saveLoading}
                                    sx={{ color: "darkgreen !important", border: '1px solid rgba(0, 100, 0, 0.5) !important' }}
                                    onClick={() => filterImg("save")}
                                    loading={saveLoading}
                                />

                                <div style={{ margin: state?.teacherVerify_edit ? '10px 0' : '0 5px' }} />
                            </>
                        )} */}

                        {/* {userData?.role == 'student' && ( */}
                        {(isDownloaded==0 || role === "parent") && (
                            <>
                                <StandardButton
                                    label={"Edit"}
                                    variant="outlined"
                                    bg="transparent"
                                    sx={{ color: "darkgreen !important", border: '1px solid rgba(0, 100, 0, 0.5) !important' }}
                                    onClick={() => {
                                        // Collect all links created by createlink API (filter out data URLs)
                                        const allImageData = { ...state.img_data?.updateDataImage, ...state.img_data?.updateDataImageBack };
                                        const createdLinks = {};
                                        
                                        Object.keys(allImageData).forEach(key => {
                                            const imgValue = allImageData[key];
                                            // Only include actual links (not data URLs)
                                            if (imgValue && imgValue.split(':')[0] !== 'data') {
                                                createdLinks[key] = imgValue;
                                            }
                                        });
                                        
                                        console.log("Sending createdLinks to Update:", createdLinks);
                                        
                                        // Navigate back to Update page with links using history.push to properly pass state
                                        history.push("/update", {
                                            ...state,
                                            createdLinks: createdLinks
                                        });
                                    }}
                                />

                                <div style={{ margin: '0 5px' }} />
                            </>
                        )}
                        {/* )} */}
                        {(isDownloaded==0 || role === "parent") && (
                            <StandardButton
                                // label={state?.teacherVerify_edit ? "Final For Print" : "Submit"}
                                // onClick={() => filterImg("submit")}
                                label={role === "parent" ? "Submit" : "Final For Print"}
                                type="submit"
                                onClick={onFinalForPrint}
                                loading={btnLoading}
                                disabled={btnLoading || saveLoading}
                            />
                        )}
                        {isDownloaded==1   && (
                            <Typography
                                sx={{
                                    color: 'error.main',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    mb: 4,
                                    ml:10
                                }}
                            >
                                Already downloaded for print
                            </Typography>
                        )}
                    </div>
                </section>
            </div>
        </IonicPageWrapper>
    );
}

export default PreviewPage